import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Building, Briefcase, HelpCircle, Calendar, Euro, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TENANT_TYPE_OPTIONS = [
  { value: 'Particulier', label: 'Particulier', icon: Users },
  { value: 'Zakelijk', label: 'Zakelijk', icon: Briefcase },
  { value: 'Gelieerd', label: 'Gelieerd', icon: Building },
  { value: 'Onbekend', label: 'Onbekend', icon: HelpCircle }
];

const KREDIET_OPTIONS = [
  { value: 'Sterk', label: 'Sterk', color: 'bg-green-500' },
  { value: 'Gemiddeld', label: 'Gemiddeld', color: 'bg-yellow-500' },
  { value: 'Zwak', label: 'Zwak', color: 'bg-red-500' },
  { value: 'Onbekend', label: 'Onbekend', color: 'bg-slate-500' }
];

const CONTRACT_TYPE_OPTIONS = ['ROZ Kantoor', 'ROZ Winkel', 'ROZ Bedrijfsruimte', 'Maatwerk', 'Onbekend'];
const INDEXATIE_OPTIONS = ['CPI', 'Vast %', 'Geen', 'Onbekend'];
const RISICO_OPTIONS = [
  { value: 'Laag', color: 'bg-green-500' },
  { value: 'Midden', color: 'bg-yellow-500' },
  { value: 'Hoog', color: 'bg-red-500' },
  { value: 'Onbekend', color: 'bg-slate-500' }
];

const initialForm = {
  tenant_naam: '',
  tenant_type: 'Onbekend',
  kvk_nummer: '',
  kredietindicatie: 'Onbekend',
  contact_email: '',
  contact_phone: '',
  contract_type: 'Onbekend',
  startdatum: '',
  einddatum: '',
  optietermijnen: '',
  huurprijs_per_jaar: '',
  servicekosten_per_jaar: '',
  incentives_per_jaar: '',
  waarborgsom: '',
  gehuurde_oppervlakte_m2: '',
  indexatie_type: 'Onbekend',
  indexatie_percentage: '',
  break_optie: false,
  break_datum: '',
  leegstand_risico: 'Onbekend',
  is_leegstand: false
};

export const TenantsTab = ({ buildingId, onTenantsChange, t }) => {
  const [huurders, setHuurders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHuurder, setEditingHuurder] = useState(null);
  const [form, setForm] = useState(initialForm);

  const fetchHuurders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/huurders/realestate/${buildingId}`, {
        withCredentials: true
      });
      setHuurders(res.data);
      if (onTenantsChange) onTenantsChange(res.data);
    } catch (err) {
      console.error('Error fetching huurders:', err);
    } finally {
      setLoading(false);
    }
  }, [buildingId, onTenantsChange]);

  useEffect(() => {
    if (buildingId) {
      fetchHuurders();
    }
  }, [buildingId, fetchHuurders]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingHuurder(null);
  };

  const openDialog = (huurder = null) => {
    if (huurder) {
      setEditingHuurder(huurder);
      setForm({
        tenant_naam: huurder.tenant_naam || '',
        tenant_type: huurder.tenant_type || 'Onbekend',
        kvk_nummer: huurder.kvk_nummer || '',
        kredietindicatie: huurder.kredietindicatie || 'Onbekend',
        contact_email: huurder.contact_email || '',
        contact_phone: huurder.contact_phone || '',
        contract_type: huurder.contract_type || 'Onbekend',
        startdatum: huurder.startdatum?.split('T')[0] || '',
        einddatum: huurder.einddatum?.split('T')[0] || '',
        optietermijnen: huurder.optietermijnen || '',
        huurprijs_per_jaar: huurder.huurprijs_per_jaar || '',
        servicekosten_per_jaar: huurder.servicekosten_per_jaar || '',
        incentives_per_jaar: huurder.incentives_per_jaar || '',
        waarborgsom: huurder.waarborgsom || '',
        gehuurde_oppervlakte_m2: huurder.gehuurde_oppervlakte_m2 || '',
        indexatie_type: huurder.indexatie_type || 'Onbekend',
        indexatie_percentage: huurder.indexatie_percentage || '',
        break_optie: huurder.break_optie || false,
        break_datum: huurder.break_datum?.split('T')[0] || '',
        leegstand_risico: huurder.leegstand_risico || 'Onbekend',
        is_leegstand: huurder.is_leegstand || false
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      realestate_id: buildingId,
      huurprijs_per_jaar: form.huurprijs_per_jaar ? parseFloat(form.huurprijs_per_jaar) : 0,
      servicekosten_per_jaar: form.servicekosten_per_jaar ? parseFloat(form.servicekosten_per_jaar) : 0,
      incentives_per_jaar: form.incentives_per_jaar ? parseFloat(form.incentives_per_jaar) : 0,
      waarborgsom: form.waarborgsom ? parseFloat(form.waarborgsom) : null,
      gehuurde_oppervlakte_m2: form.gehuurde_oppervlakte_m2 ? parseFloat(form.gehuurde_oppervlakte_m2) : 0,
      indexatie_percentage: form.indexatie_percentage ? parseFloat(form.indexatie_percentage) : null
    };

    try {
      if (editingHuurder) {
        await axios.put(`${API_URL}/api/huurders/${editingHuurder.huurder_id}`, payload, {
          withCredentials: true
        });
        toast.success('Huurder bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/huurders`, payload, {
          withCredentials: true
        });
        toast.success('Huurder toegevoegd');
      }
      setDialogOpen(false);
      resetForm();
      fetchHuurders();
    } catch (err) {
      console.error('Error saving huurder:', err);
      toast.error('Fout bij opslaan huurder');
    }
  };

  const handleDelete = async (huurderId) => {
    if (!window.confirm('Weet je zeker dat je deze huurder wilt verwijderen?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/huurders/${huurderId}`, {
        withCredentials: true
      });
      toast.success('Huurder verwijderd');
      fetchHuurders();
    } catch (err) {
      console.error('Error deleting huurder:', err);
      toast.error('Fout bij verwijderen huurder');
    }
  };

  const getTypeIcon = (type) => {
    const opt = TENANT_TYPE_OPTIONS.find(o => o.value === type);
    if (opt) {
      const Icon = opt.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <HelpCircle className="h-4 w-4" />;
  };

  const getKredietBadge = (krediet) => {
    const opt = KREDIET_OPTIONS.find(o => o.value === krediet);
    return (
      <Badge className={`${opt?.color || 'bg-slate-500'} text-white`}>
        {krediet}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `€ ${parseFloat(value).toLocaleString('nl-NL', { minimumFractionDigits: 0 })}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('nl-NL');
  };

  // Calculate totals
  const totalRentYearly = huurders.filter(h => !h.is_leegstand).reduce((sum, h) => sum + (h.huurprijs_per_jaar || 0), 0);
  const totalSqm = huurders.reduce((sum, h) => sum + (h.gehuurde_oppervlakte_m2 || 0), 0);

  return (
    <div className="space-y-4" data-testid="tenants-tab">
      {/* Summary */}
      <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-slate-400 text-sm">Huurders</span>
            <p className="text-2xl font-bold text-white">{huurders.filter(h => !h.is_leegstand).length}</p>
          </div>
          <div className="border-l border-slate-700 pl-6">
            <span className="text-slate-400 text-sm">Totaal Huur/Jaar</span>
            <p className="text-xl font-bold text-[#b4976f]">{formatCurrency(totalRentYearly)}</p>
          </div>
          <div className="border-l border-slate-700 pl-6">
            <span className="text-slate-400 text-sm">Totaal m²</span>
            <p className="text-xl font-bold text-white">{totalSqm.toLocaleString('nl-NL')}</p>
          </div>
          <div className="border-l border-slate-700 pl-6">
            <span className="text-slate-400 text-sm">Leegstand</span>
            <p className="text-xl font-bold text-red-400">{huurders.filter(h => h.is_leegstand).length}</p>
          </div>
        </div>
        <Button 
          onClick={() => openDialog()} 
          className="bg-[#b4976f] hover:bg-[#a3865f] text-white"
          data-testid="add-huurder-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Huurder Toevoegen
        </Button>
      </div>

      {/* Huurders Table */}
      {loading && huurders.length === 0 ? (
        <div className="text-center py-8 text-slate-400">Laden...</div>
      ) : huurders.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 rounded-lg border border-dashed border-slate-700">
          <Users className="h-12 w-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 mb-4">Nog geen huurders geregistreerd</p>
          <Button 
            onClick={() => openDialog()}
            variant="outline"
            className="border-[#b4976f] text-[#b4976f] hover:bg-[#b4976f]/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Eerste Huurder Toevoegen
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800 hover:bg-slate-800">
                <TableHead className="text-slate-300">Naam</TableHead>
                <TableHead className="text-slate-300">Type</TableHead>
                <TableHead className="text-slate-300 text-right">Huur/Jaar</TableHead>
                <TableHead className="text-slate-300 text-right">m²</TableHead>
                <TableHead className="text-slate-300">Contract</TableHead>
                <TableHead className="text-slate-300">Einddatum</TableHead>
                <TableHead className="text-slate-300">Krediet</TableHead>
                <TableHead className="text-slate-300 text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {huurders.map((huurder) => (
                <TableRow key={huurder.huurder_id} className="border-slate-700 hover:bg-slate-800/50">
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(huurder.tenant_type)}
                      {huurder.tenant_naam}
                      {huurder.is_leegstand && (
                        <Badge className="bg-red-500 text-white ml-2">Leegstand</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">{huurder.tenant_type}</TableCell>
                  <TableCell className="text-[#b4976f] text-right font-medium">
                    {formatCurrency(huurder.huurprijs_per_jaar)}
                  </TableCell>
                  <TableCell className="text-slate-300 text-right">
                    {huurder.gehuurde_oppervlakte_m2?.toLocaleString('nl-NL') || '-'}
                  </TableCell>
                  <TableCell className="text-slate-300">{huurder.contract_type}</TableCell>
                  <TableCell className="text-slate-300">{formatDate(huurder.einddatum)}</TableCell>
                  <TableCell>{getKredietBadge(huurder.kredietindicatie)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog(huurder)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(huurder.huurder_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Huurder Dialog - Full unified form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-[#b4976f]" />
              {editingHuurder ? 'Huurder Bewerken' : 'Nieuwe Huurder'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tenant Identity Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2">Huurder Gegevens</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Naam Huurder *</Label>
                  <Input
                    value={form.tenant_naam}
                    onChange={(e) => setForm({ ...form, tenant_naam: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Naam huurder of bedrijf"
                    required
                    data-testid="huurder-naam-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Type</Label>
                  <Select value={form.tenant_type} onValueChange={(v) => setForm({ ...form, tenant_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {TENANT_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-800">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">KVK Nummer</Label>
                  <Input
                    value={form.kvk_nummer}
                    onChange={(e) => setForm({ ...form, kvk_nummer: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="12345678"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Kredietindicatie</Label>
                  <Select value={form.kredietindicatie} onValueChange={(v) => setForm({ ...form, kredietindicatie: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {KREDIET_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-800">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Telefoon</Label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Contract Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Huurcontract
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Contract Type</Label>
                  <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {CONTRACT_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Startdatum</Label>
                  <Input
                    type="date"
                    value={form.startdatum}
                    onChange={(e) => setForm({ ...form, startdatum: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Einddatum</Label>
                  <Input
                    type="date"
                    value={form.einddatum}
                    onChange={(e) => setForm({ ...form, einddatum: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Optietermijnen</Label>
                <Input
                  value={form.optietermijnen}
                  onChange={(e) => setForm({ ...form, optietermijnen: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Bijv. 2x5 jaar"
                />
              </div>
            </div>

            {/* Financial Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2 flex items-center gap-2">
                <Euro className="h-4 w-4" />
                Financieel (per jaar)
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Huurprijs (€/jaar) *</Label>
                  <Input
                    type="number"
                    value={form.huurprijs_per_jaar}
                    onChange={(e) => setForm({ ...form, huurprijs_per_jaar: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="0"
                    required
                    data-testid="huurprijs-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Servicekosten (€/jaar)</Label>
                  <Input
                    type="number"
                    value={form.servicekosten_per_jaar}
                    onChange={(e) => setForm({ ...form, servicekosten_per_jaar: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Incentives (€/jaar)</Label>
                  <Input
                    type="number"
                    value={form.incentives_per_jaar}
                    onChange={(e) => setForm({ ...form, incentives_per_jaar: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Waarborgsom (€)</Label>
                  <Input
                    type="number"
                    value={form.waarborgsom}
                    onChange={(e) => setForm({ ...form, waarborgsom: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Gehuurde m²</Label>
                  <Input
                    type="number"
                    value={form.gehuurde_oppervlakte_m2}
                    onChange={(e) => setForm({ ...form, gehuurde_oppervlakte_m2: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Leegstand Risico</Label>
                  <Select value={form.leegstand_risico} onValueChange={(v) => setForm({ ...form, leegstand_risico: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {RISICO_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-800">{opt.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Indexation Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2">Indexatie & Opties</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Indexatie Type</Label>
                  <Select value={form.indexatie_type} onValueChange={(v) => setForm({ ...form, indexatie_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {INDEXATIE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-800">{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.indexatie_type === 'Vast %' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Indexatie %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.indexatie_percentage}
                      onChange={(e) => setForm({ ...form, indexatie_percentage: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="2.5"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
                <Label className="text-slate-300">Break Optie</Label>
                <Switch
                  checked={form.break_optie}
                  onCheckedChange={(v) => setForm({ ...form, break_optie: v })}
                />
                {form.break_optie && (
                  <div className="flex-1">
                    <Input
                      type="date"
                      value={form.break_datum}
                      onChange={(e) => setForm({ ...form, break_datum: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white max-w-xs"
                      placeholder="Break datum"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
                <Label className="text-slate-300">Dit is leegstand</Label>
                <Switch
                  checked={form.is_leegstand}
                  onCheckedChange={(v) => setForm({ ...form, is_leegstand: v })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
                Annuleren
              </Button>
              <Button type="submit" className="bg-[#b4976f] hover:bg-[#a3865f] text-white" data-testid="save-huurder-btn">
                {editingHuurder ? 'Bijwerken' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantsTab;
